import {
    AssetCustomView,
    AssetCustomViewDto,
    CreateAssetCustomView,
    CreateAssetCustomViewDto,
    toAssetCustomView,
    toCreateAssetViewDto,
} from "domain/asset-management/assets";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { ParameterQuery } from "utils/commonFunctions";

export interface ManifestDto {
    manifest_uuid: string;
    custom_view_uuid: string;
    description?: string;
    erasure_method?: string;
    erasure_notes?: string;
    organization_name?: string;
    pickup_request_date?: string;
    location?: string;
    address?: string;
    primary_site_contact?: string;
    primary_site_number?: string;
    primary_site_email?: string;
    alternative_site_contact?: string;
    alternative_site_number?: string;
    alternative_site_email?: string;
    organization_id?: string;
    disposition_instructions?: string;
    manifest_notes?: string;
    asset_count?: string;
    weight?: string;
    transporter_company?: string;
    container_type?: string;
    container_id?: string;
}

export interface Manifest {
    manifestUuid: string;
    customViewUuid: string;
    description?: string;
    erasureMethod?: string;
    erasureNotes?: string;
    organizationName?: string;
    pickupRequestDate?: string;
    location?: string;
    address?: string;
    primarySiteContact?: string;
    primarySiteNumber?: string;
    primarySiteEmail?: string;
    alternativeSiteContact?: string;
    alternativeSiteNumber?: string;
    alternativeSiteEmail?: string;
    organizationId?: string;
    dispositionInstructions?: string;
    manifestNotes?: string;
    assetCount?: string;
    weight?: string;
    transporterCompany?: string;
    containerType?: string;
    containerId?: string;
}

function toManifest(dto: ManifestDto): Manifest {
    return {
        manifestUuid: dto.manifest_uuid,
        customViewUuid: dto.custom_view_uuid,
        description: dto.description || undefined,
        erasureMethod: dto.erasure_method || undefined,
        erasureNotes: dto.erasure_notes || undefined,
        organizationName: dto.organization_name || undefined,
        pickupRequestDate: dto.pickup_request_date || undefined,
        location: dto.location || undefined,
        address: dto.address || undefined,
        primarySiteContact: dto.primary_site_contact || undefined,
        primarySiteNumber: dto.primary_site_number || undefined,
        primarySiteEmail: dto.primary_site_email || undefined,
        alternativeSiteContact: dto.alternative_site_contact || undefined,
        alternativeSiteNumber: dto.alternative_site_number || undefined,
        alternativeSiteEmail: dto.alternative_site_email || undefined,
        organizationId: dto.organization_id || undefined,
        dispositionInstructions: dto.disposition_instructions || undefined,
        manifestNotes: dto.manifest_notes || undefined,
        assetCount: dto.asset_count || undefined,
        weight: dto.weight || undefined,
        transporterCompany: dto.transporter_company || undefined,
        containerType: dto.container_type || undefined,
        containerId: dto.container_id || undefined,
    };
}

interface RemoveAssetsFromGroupDto {
    group_uuid: string;
    assets_to_remove: string[];
}
const PATH_API_V1_REPORT_VIEWS = "/api/v1/report-views";
const ASSET_MANAGEMENT_PATH = "/api/v1/asset-management/";

export class AssetViewService {
    async fetchAssetRelatedViews(): Promise<AssetCustomView[]> {
        try {
            const response = await apiGatewayService.invokeApi<null, { asset_views: AssetCustomViewDto[] }>(
                "/api/v1/report-views?type=asset",
                "GET",
                null,
                {
                    apiType: ApiType.ARTHUR,
                    refreshSession: true,
                }
            );
            return (response.asset_views || []).map((each) => toAssetCustomView(each));
        } catch (error) {
            console.error("Failed to fetch asset views from backend:", error);
            return [];
        }
    }

    async createAssetCustomView(view: CreateAssetCustomView): Promise<AssetCustomViewDto> {
        try {
            return await apiGatewayService.invokeApi<CreateAssetCustomViewDto, AssetCustomViewDto>(
                "/api/v1/report-views?type=asset",
                "POST",
                toCreateAssetViewDto(view),
                { apiType: ApiType.ARTHUR, refreshSession: true, emptyResponse: true }
            );
        } catch (error) {
            console.error("Failed to create asset view:", error);
            throw error;
        }
    }

    async addAssetsToGroup(groupUuid: string, assetIds: string[]): Promise<void> {
        return apiGatewayService.invokeApi(
            ASSET_MANAGEMENT_PATH + "assets/groups/add",
            "POST",
            { group_uuid: groupUuid, assets: assetIds },
            { apiType: ApiType.OLIVER, refreshSession: true, emptyResponse: true }
        );
    }

    async editAssetCustomView(
        uuid: string,
        view: CreateAssetCustomView,
        abortController: AbortController
    ): Promise<void> {
        return apiGatewayService.invokeApi(
            PATH_API_V1_REPORT_VIEWS + "/" + uuid + "?type=asset",
            "POST",
            toCreateAssetViewDto(view),
            {
                abortSignal: abortController.signal,
                apiType: ApiType.ARTHUR,
                refreshSession: true,
            }
        );
    }

    public deleteAssetView(uuid: string, abortController: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(PATH_API_V1_REPORT_VIEWS + "/" + uuid + "?type=asset", "DELETE", null, {
            abortSignal: abortController.signal,
            refreshSession: true,
            apiType: ApiType.ARTHUR,
            emptyResponse: true,
        });
    }

    async moveAssetCustomView(
        uuid: string,
        targetFolderUuid: string | null,
        abortController: AbortController
    ): Promise<void> {
        return apiGatewayService.invokeApi(
            `${PATH_API_V1_REPORT_VIEWS}/${uuid}/move?type=asset`,
            "POST",
            { parent_uuid: targetFolderUuid },
            {
                abortSignal: abortController.signal,
                apiType: ApiType.ARTHUR,
                refreshSession: true,
                emptyResponse: true,
            }
        );
    }

    async archiveAssetView(uuid: string, abortController: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(`${PATH_API_V1_REPORT_VIEWS}/${uuid}/archive/?type=asset`, "POST", null, {
            abortSignal: abortController.signal,
            apiType: ApiType.ARTHUR,
            refreshSession: true,
            emptyResponse: true,
        });
    }

    async fetchManifest(customViewUuid: string, abortController?: AbortController): Promise<Manifest> {
        const dto = await apiGatewayService.invokeApi<null, ManifestDto>(
            `/api/v1/asset-management/manifests/${encodeURIComponent(customViewUuid)}`,
            "GET",
            null,
            {
                abortSignal: abortController?.signal,
                jsonResponse: true,
                apiType: ApiType.OLIVER,
            }
        );
        return toManifest(dto);
    }

    async updateManifest(
        customViewUuid: string,
        data: Partial<ManifestDto>,
        abortController?: AbortController
    ): Promise<void> {
        await apiGatewayService.invokeApi(
            `/api/v1/asset-management/manifests/${encodeURIComponent(customViewUuid)}`,
            "POST",
            data,
            {
                abortSignal: abortController?.signal,
                emptyResponse: true,
                apiType: ApiType.OLIVER,
            }
        );
    }

    public detachFromFolder(abortController: AbortController, customViewUuid: string): Promise<void> {
        const url = `${PATH_API_V1_REPORT_VIEWS}/${customViewUuid}/detach/?type=asset`;
        return apiGatewayService.invokeApi(url, "POST", null, {
            abortSignal: abortController.signal,
            emptyResponse: true,
            apiType: ApiType.ARTHUR,
        });
    }

    public removeAssetsFromGroup(
        groupUuid: string,
        assetsToRemove: string[],
        abortController: AbortController,
        all?: boolean
    ): Promise<void> {
        const body: RemoveAssetsFromGroupDto = {
            group_uuid: groupUuid,
            assets_to_remove: assetsToRemove,
        };
        const url =
            ASSET_MANAGEMENT_PATH +
            "assets/groups/remove" +
            new ParameterQuery().add("all", all ? "true" : undefined).createQueryString();
        return apiGatewayService.invokeApi(url, "POST", body, {
            abortSignal: abortController.signal,
            emptyResponse: true,
            apiType: ApiType.OLIVER,
        });
    }
}
export const assetViewService = new AssetViewService();
